/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import {getDeviceConnectionState} from '@ion/core/src/services/Connect';
import {DeviceConnectionStateResult} from '@ion/core/src/types/services';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type ConnectionStatusMap = Record<string, boolean>;

export type CleanSessionMap = Record<string, boolean>;

export interface DeviceConnectionStateMaps {
  connectionStatusMap: ConnectionStatusMap;
  cleanSessionMap: CleanSessionMap;
}

export type UseVehicleMqttConnectionStatusResult = UseDefaultQueryResult & DeviceConnectionStateMaps & {
  refresh: () => void;
};

const toDeviceConnectionStateMaps = (
  results: DeviceConnectionStateResult[]
): DeviceConnectionStateMaps => (
  results.reduce<DeviceConnectionStateMaps>((acc, item) => {
    acc.connectionStatusMap[item.esn] = item.status === 'CONNECTED';
    acc.cleanSessionMap[item.esn]     = item.cleanSessionInProgress === true;
    return acc;
  }, {connectionStatusMap: {}, cleanSessionMap: {}})
);

/**
 * Retrieves MQTT connection status and clean session state for a list of ESNs
 * from the DM gateway. Results are cached per ESN list, so callers passing
 * the same ESNs share one request.
 *
 * - CONNECTED maps to true; any other status or missing result maps to false.
 * - `cleanSessionInProgress` maps to true only when DM reports `true`.
 * - On request failure all vehicles remain disconnected (retry: false).
 *
 * ```typescript
 * import useVehicleMqttConnectionStatus from
 *   '@ion/query/src/hooks/useVehicleMqttConnectionStatus';
 *
 * const {
 *   connectionStatusMap,
 *   cleanSessionMap
 * } = useVehicleMqttConnectionStatus(esns);
 * const isConnected = connectionStatusMap[esn] ?? false;
 * const isCleaning  = cleanSessionMap[esn] ?? false;
 * ```
 *
 * @param esns Stable array of ESNs for the current grid page (max 100).
 */
const useVehicleMqttConnectionStatus = (
  esns: string[]
): UseVehicleMqttConnectionStatusResult => {
  const queryClient = useQueryClient();

  const key = useMemo(() => (
    createKey(QueryKey.VEHICLE_CONNECTION_STATUS, esns)
  ), [esns]);

  const query = useQuery<DeviceConnectionStateMaps>(key, async() => {
    const response = await getDeviceConnectionState(esns.join(','));
    const results  = response?.response?.results || [];
    return toDeviceConnectionStateMaps(results);
  }, {
    staleTime : getStaleTime(1, false, false) as number,
    enabled   : esns.length > 0,
    retry     : false
  });

  const refresh = useCallback(() => {
    queryClient.invalidateQueries(key);
  }, [key]);

  return createQueryResult(query, {
    connectionStatusMap : query.data?.connectionStatusMap || {},
    cleanSessionMap     : query.data?.cleanSessionMap || {},
    refresh
  }) as UseVehicleMqttConnectionStatusResult;
};

export default useVehicleMqttConnectionStatus;
